import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { LockKey, X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRIGGER = "1001";

// Robust: tracks a rolling buffer of the last digits pressed.
// Does NOT reset on non-digit keys, only on a 6s silence window.
// Mounted at document level so it survives clicks anywhere.
export const OwnerSecret = () => {
    const [open, setOpen] = useState(false);
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const bufferRef = useRef("");
    const timeoutRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const onKey = (e) => {
            // Ignore when typing in inputs / textareas / contenteditable
            const target = e.target;
            const tag = (target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select") return;
            if (target?.isContentEditable) return;

            // Backup keyboard shortcut: Ctrl+Shift+O (or Cmd+Shift+O on mac)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "O" || e.key === "o")) {
                e.preventDefault();
                setOpen(true);
                return;
            }

            // Only digits contribute to the secret buffer
            if (!/^[0-9]$/.test(e.key)) return;

            bufferRef.current = (bufferRef.current + e.key).slice(-12);

            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                bufferRef.current = "";
            }, 6000);

            if (bufferRef.current.endsWith(TRIGGER)) {
                bufferRef.current = "";
                setOpen(true);
            }
        };

        // Attach to document with capture so iframes & focused buttons don't swallow it
        document.addEventListener("keydown", onKey, true);
        // Also expose a console escape hatch just in case
        window.__oolOwnerUnlock = () => setOpen(true);

        return () => {
            document.removeEventListener("keydown", onKey, true);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            delete window.__oolOwnerUnlock;
        };
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/owner/verify`, { code: code.trim() });
            sessionStorage.setItem("ool_owner_code", code.trim());
            toast.success("Owner access granted.");
            setOpen(false);
            setCode("");
            navigate("/owner");
        } catch (err) {
            toast.error("Invalid code.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
                    onClick={() => setOpen(false)}
                    data-testid="owner-secret-modal"
                >
                    <motion.form
                        initial={{ scale: 0.92, y: 20, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.92, y: 20, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={onSubmit}
                        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-7"
                    >
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="absolute top-4 right-4 text-white/40 hover:text-white"
                            aria-label="Close"
                            data-testid="owner-secret-close"
                        >
                            <X size={18} />
                        </button>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="h-10 w-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                                <LockKey size={18} className="text-cyan-300" weight="bold" />
                            </div>
                            <div>
                                <div className="eyebrow text-cyan-300/70">Owner access</div>
                                <div className="font-display text-white text-lg">Enter code</div>
                            </div>
                        </div>
                        <input
                            type="password"
                            inputMode="numeric"
                            autoFocus
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="• • •"
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white text-center font-mono text-2xl tracking-[0.5em] focus:border-cyan-400/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/20"
                            data-testid="owner-secret-input"
                        />
                        <button
                            type="submit"
                            disabled={submitting}
                            className="mt-5 w-full py-3 rounded-xl bg-white text-black font-semibold hover:bg-cyan-300 transition disabled:opacity-60"
                            data-testid="owner-secret-submit"
                        >
                            {submitting ? "Verifying..." : "Unlock"}
                        </button>
                        <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-white/30 text-center">
                            Restricted area
                        </p>
                    </motion.form>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
